import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AGGREGATE_NAMES, HTTP_STATUS, LOAN_REQUEST_COMMANDS, USER_RESOLVERS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator';
import { invalidateAdvisorServiceQueries } from '~/features/leadDetails/utils/invalidateAdvisorServiceQueries';

// 404: the picked user was deactivated or locked meanwhile → stale data like 400/403
const STALE_STATUSES: Array<number | undefined> = [
    HTTP_STATUS.BAD_REQUEST,
    HTTP_STATUS.FORBIDDEN,
    HTTP_STATUS.NOT_FOUND,
];

// non-IK shared lead: the added advisor hands their share to another user, the owner stays
export const useChangeAddedAdvisorMutation = (loanRequestId: string) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (userId: string) =>
            APICommunicator.sendCommand({
                aggregateId: loanRequestId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.changeAssignedUser,
                payload: { userId },
            }),
        onError: async (error: Error & { status?: number }) => {
            if (STALE_STATUSES.includes(error.status)) {
                toast.error(t('LEADS.ADVISOR_SERVICE.NOT_PERMITTED'));
                await Promise.all([
                    invalidateAdvisorServiceQueries(queryClient, loanRequestId),
                    queryClient.invalidateQueries({
                        exact: false,
                        queryKey: [AGGREGATE_NAMES.USER, USER_RESOLVERS.getAdvisersByRole],
                    }),
                ]);
                return;
            }
            toast.error(t('LEADS.ADVISOR_SERVICE.CHANGE_ERROR'));
        },
        onSuccess: async ({ position }) => {
            await invalidateAdvisorServiceQueries(queryClient, loanRequestId, position);
            toast.success(t('LEADS.ADVISOR_SERVICE.CHANGE_SUCCESS'));
        },
    });
};
